<template>
	<DashboardCard icon="military_tech" :heading="messages.heading">
		<div class="grid space-y-6">
			<DashboardMetricWithIcon
				:icon="statusDisplayIcon"
				:value="statusDisplayName"
				:label="messages.metrics.latestHdnStatus"
			/>
			<DashboardMetric
				:label="messages.metrics.description"
				:value="props.statusMessage ?? getStatusDescription(props.status)"
			/>
		</div>
	</DashboardCard>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { isKeyOf } from "@/helpers/typeHelpers";

const props = defineProps<{
	status: string;
	statusMessage?: string | null;
}>();

const messages = {
	heading: "Status",
	metrics: {
		latestHdnStatus: "Actuele HDN-status",
		description: "Omschrijving",
	},
};

const statusDisplayName = computed(() => {
	const statusName = getStatusName(props.status);
	const statusCode = getStatusCode(props.status);
	const suffix = statusCode === null ? "" : `(SX${statusCode})`;
	return `${statusName} ${suffix}`;
});

const statusDisplayIcon = computed(() => {
	return getStatusIcon(props.status);
});

// TODO: extract rest of file to composable or utility function
// ... should utilities be stored in helper folder?
type HdnApplicationStatus =
	| "01 Offertetermijn verloopt"
	| "04 Dossier ter acceptatie"
	| "05 Dossier finaal akkoord"
	| "09 Offerteaanvraag ontvangen"
	| "10 Offerteaanvraag in behandeling genomen"
	| "11 Offerteaanvraag afgewezen"
	| "12 Wachten op (aanvullende) stukken"
	| "14 Offerteaanvraag niet te verwerken, zie toelichting"
	| "16 Offerteaanvraag beëindigd, zie toelichting"
	| "17 Inkomen akkoord"
	| "18 Onderpand akkoord"
	| "19 Verplichtingen akkoord"
	| "20 Lening in beheer"
	| "22 Renteaanbod verzonden"
	| "23 Bindende offerte verzonden"
	| "24 Offerte aanvraag wordt aangepast door geldverstrekker of serviceprovider"
	| "25 Offerte aanvraag technisch niet te verwerken"
	| "26 Gelden worden uitbetaald"
	| "27 Indicatief voorstel verlengd"
	| "28 Offerte verlengd"
	| "29 Dossier aangeboden voor finaal akkoord"
	| "30 Herbeoordeling na tweede beoordeling"
	| "31 Dossier verzonden naar notaris"
	| "80 Partij niet geautoriseerd (zie toelichting)"
	| "81 Autorisatie niet verleend door HDN Poortwachter (zie toelichting)"
	| "82 Aanroep HDN Poortwachter mislukt. Bericht wel verzonden";

type LLApplicationStatus = HdnApplicationStatus | "draft" | "sent" | "unknown";

// gets numerical code prefix from application status
function getStatusCode(status: string) {
	if (["draft", "sent"].includes(status)) {
		return null;
	}
	return splitHdnValue(props.status)[0];
}

/**
 * Gets displayable name for the application status.
 * If no override then the suffix part of application status is used.
 */
function getStatusName(status: string) {
	const statusToName: Record<string, string> = {
		draft: "Concept",
		sent: "Verzonden",
	};
	return status in statusToName
		? statusToName[status]
		: splitHdnValue(props.status)[1];
}

/**
 * Get a MUI icon name associated with status.
 * Has a fallback if no icon is defined for given status.
 */
function getStatusIcon(status: string) {
	const statusIconNames: Record<string, string> = {
		draft: "trip_origin",
		unknown: "help",
		pending: "pending",
		inspection: "visibility",
		success: "check",
		error: "cancel",
		warning: "warning",
		sent: "send",
	};

	const statusToIcon: Record<LLApplicationStatus, string> = {
		draft: "draft",
		sent: "sent",
		unknown: "unknown",
		"01 Offertetermijn verloopt": "warning",
		"04 Dossier ter acceptatie": "inspection",
		"05 Dossier finaal akkoord": "success",
		"09 Offerteaanvraag ontvangen": "pending",
		"10 Offerteaanvraag in behandeling genomen": "inspection",
		"11 Offerteaanvraag afgewezen": "error",
		"12 Wachten op (aanvullende) stukken": "pending",
		"14 Offerteaanvraag niet te verwerken, zie toelichting": "error",
		"16 Offerteaanvraag beëindigd, zie toelichting": "error",
		"17 Inkomen akkoord": "success",
		"18 Onderpand akkoord": "success",
		"19 Verplichtingen akkoord": "success",
		"20 Lening in beheer": "success",
		"22 Renteaanbod verzonden": "pending",
		"23 Bindende offerte verzonden": "pending",
		"24 Offerte aanvraag wordt aangepast door geldverstrekker of serviceprovider":
			"pending",
		"25 Offerte aanvraag technisch niet te verwerken": "error",
		"26 Gelden worden uitbetaald": "success",
		"27 Indicatief voorstel verlengd": "pending",
		"28 Offerte verlengd": "pending",
		"29 Dossier aangeboden voor finaal akkoord": "inspection",
		"30 Herbeoordeling na tweede beoordeling": "inspection",
		"31 Dossier verzonden naar notaris": "inspection",
		"80 Partij niet geautoriseerd (zie toelichting)": "error",
		"81 Autorisatie niet verleend door HDN Poortwachter (zie toelichting)":
			"error",
		"82 Aanroep HDN Poortwachter mislukt. Bericht wel verzonden": "error",
	};

	if (isKeyOf(statusToIcon, status)) {
		const iconName = statusToIcon[status];
		return statusIconNames[iconName];
	}

	return statusIconNames["unknown"];
}

/**
 * Get displayable status description.
 * Returns undefined if no description is defined for given status.
 */
function getStatusDescription(status: string) {
	const statusToDescription: Partial<Record<LLApplicationStatus, string>> = {
		draft: "Offerteaanvraag is nog niet verstuurd.",
		sent: "Offerteaanvraag is verstuurd via HDN",
	};
	if (isKeyOf(statusToDescription, status)) {
		return statusToDescription[status];
	}

	return undefined;
}
</script>
